import Intercom, { update, shutdown } from '@intercom/messenger-js-sdk';
import { fetchIntercomUserHash } from '../api/fetchers/fetchIntercomUserHash';

const INTERCOM_USER_HASH_PREFIX = 'intercomHash';

const getHashCacheKey = (id: string) => `${INTERCOM_USER_HASH_PREFIX}:${id}`;

// Tracks whether the Intercom snippet itself has been initialized yet (as
// either a visitor or an identified user). boot()/shutdown() are no-ops
// until window.Intercom exists, which only happens after calling Intercom()
// once — so the very first call this session must go through Intercom(),
// not boot().
let hasInitializedSdk = false;

export const initIntercomVisitor = () => {
  // Idempotent on purpose: isLoggedIn can transiently flip false→true in a
  // micro-frontend setup (e.g. each remote app calling acquireTokenSilent
  // on the shared MSAL instance can blip the account cache), which would
  // otherwise re-fire this from App.tsx's effect and re-send the anonymous
  // visitor boot even though the user never actually logged out.
  if (hasInitializedSdk) return;

  hasInitializedSdk = true;
  Intercom({
    app_id: process.env.REACT_APP_INTERCOM_APP_ID!,
  });
};

/**
 * Returns the Intercom Identity Verification hash for the given user.
 * Checks localStorage first (scoped by id) and only calls the hash API
 * (passing email) when no cached value exists for it, caching the freshly
 * fetched hash afterward.
 */
export const getIntercomUserHash = async (
  id: string,
  email: string,
): Promise<string | null> => {
  const cacheKey = getHashCacheKey(id);

  const cachedHash = localStorage.getItem(cacheKey);
  if (cachedHash) {
    return cachedHash;
  }

  try {
    const response = await fetchIntercomUserHash(email)(new AbortController().signal);
    const userHash = typeof response.data?.hash === 'string' && response.data.hash.length > 0
      ? response.data.hash
      : null;

    if (userHash) {
      localStorage.setItem(cacheKey, userHash);
    }

    return userHash;
  } catch (error) {
    console.error('Failed to fetch Intercom user hash', error);
    return null;
  }
};

const PING_MAX_ATTEMPTS = 2;
const PING_RETRY_DELAY_MS = 1000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type IntercomUser = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
};

const pingIntercom = (userHash: string, user: IntercomUser) => {
  const identity = {
    app_id: process.env.REACT_APP_INTERCOM_APP_ID!,
    email: user.email,
    user_id: user.email,
    name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    user_hash: userHash,
  };

  if (!hasInitializedSdk) {
    // Nothing has loaded the Intercom snippet yet (the user was already
    // logged in on page load, so the anonymous visitor boot was skipped) —
    // update() would silently no-op since window.Intercom doesn't exist
    // yet. Initialize directly with the identity so the very first call
    // is already identified.
    hasInitializedSdk = true;
    Intercom(identity);
    return;
  }

  // Already running, as a visitor or previously identified — update in
  // place instead of shutdown()+boot(), which hides and re-shows the
  // launcher (the flicker). update() is safe to call repeatedly.
  update(identity);
};

export const identifyIntercomUser = async (user: IntercomUser) => {
  const userHash = await getIntercomUserHash(user.id, user.email);

  if (!userHash) {
    // No valid hash available — don't identify with a broken/missing hash,
    // since that will fail Identity Verification if enforcement is on.
    // Fall back to the visitor session so the widget is at least present.
    console.error(
      `Skipping Intercom identify for ${user.email}: no user_hash available`,
    );
    initIntercomVisitor();
    return;
  }

  for (let attempt = 1; attempt <= PING_MAX_ATTEMPTS; attempt += 1) {
    try {
      pingIntercom(userHash, user);
      return;
    } catch (error) {
      console.error(
        `Intercom ping attempt ${attempt}/${PING_MAX_ATTEMPTS} for ${user.email} failed`,
        error,
      );

      if (attempt < PING_MAX_ATTEMPTS) {
        await delay(PING_RETRY_DELAY_MS * attempt);
      }
    }
  }

  console.error(
    `Giving up on Intercom ping for ${user.email} after ${PING_MAX_ATTEMPTS} attempts`,
  );
  // Identify never succeeded — fall back to the visitor session rather
  // than leaving the widget uninitialized/absent.
  initIntercomVisitor();
};

/**
 * Clears all cached Intercom hashes (in case multiple users have logged in
 * on this device/browser) and shuts down the current Messenger session.
 * Re-boots Intercom anonymously afterward so the widget stays available
 * for a logged-out visitor.
 */
export const logoutIntercom = () => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(`${INTERCOM_USER_HASH_PREFIX}:`))
    .forEach((key) => localStorage.removeItem(key));

  shutdown();
  // Explicit logout is a real reset — clear the guard so the next
  // initIntercomVisitor() call actually re-initializes as anonymous.
  hasInitializedSdk = false;
  initIntercomVisitor();
};
